import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type DataMessageType,
  type ObjectUpdateProposal,
  type TerraUIMessage,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { readObjectValue } from '../../utils/readObjectValue';
import { isObjectUpdateConsumed } from '../../store/consumedObjectUpdatesStore';
import { isChatEditableObjectKind } from '../../utils/chatEditableObjectUtils';
import {
  CREDENTIAL_OBJECT_KIND,
  CUSTOM_ROLE_CREATE_DRAFT_ID,
  CUSTOM_ROLE_OBJECT_KIND,
  type ChatEditableObjectKind,
} from '../../utils/chatEditableObjectUtils';

export type ParsedObjectUpdate = ObjectUpdateProposal & {
  id: string;
};

const MAX_OBJECT_UPDATE_PARSE_DEPTH = 8;
const PROPOSE_FINDING_UPDATE_TOOL = 'propose-finding-update';
const RERUN_FINDING_CVSS_TOOL = 'rerun-finding-cvss';
const MANAGE_CUSTOM_ROLES_TOOL = 'manage-custom-roles-tool';
const UPDATE_CREDENTIAL_AUTH_MATERIAL_TOOL =
  'update-credential-auth-material-tool';

const OBJECT_UPDATE_TOOL_NAMES = new Set([
  PROPOSE_FINDING_UPDATE_TOOL,
  RERUN_FINDING_CVSS_TOOL,
  MANAGE_CUSTOM_ROLES_TOOL,
  UPDATE_CREDENTIAL_AUTH_MATERIAL_TOOL,
]);

export function buildStableObjectUpdateId({
  toolCallId,
  objectKind,
  objectId,
}: {
  toolCallId: string;
  objectKind: string;
  objectId: string;
}): string {
  return `${toolCallId}:${objectKind}:${objectId}`;
}

export function readObjectUpdates({
  messages,
}: {
  messages: TerraUIMessage[];
}): ParsedObjectUpdate[] {
  const updates: ParsedObjectUpdate[] = [];
  const seenUpdateIds = new Set<string>();

  for (const message of messages) {
    for (const part of message.parts) {
      for (const update of readObjectUpdatesFromPart(part, message.id)) {
        if (seenUpdateIds.has(update.id)) {
          continue;
        }

        seenUpdateIds.add(update.id);
        updates.push(update);
      }
    }
  }

  return updates;
}

export function hasUnconsumedCustomRoleUpdatesInMessages({
  messages,
}: {
  messages: TerraUIMessage[];
}): boolean {
  for (const update of readObjectUpdates({ messages })) {
    if (update.objectKind !== CUSTOM_ROLE_OBJECT_KIND) {
      continue;
    }

    if (!isObjectUpdateConsumed(update.id)) {
      return true;
    }
  }

  return false;
}

export function hasInProgressManageCustomRolesToolInMessages({
  messages,
}: {
  messages: TerraUIMessage[];
}): boolean {
  const completedManageCustomRolesToolCallIds = new Set<string>();

  for (const message of messages) {
    if (message.role !== 'assistant') {
      continue;
    }

    for (const part of message.parts) {
      if (!isManageCustomRolesToolCallCompletePart(part)) {
        continue;
      }

      const toolCallId = readManageCustomRolesToolCallId(part);

      if (toolCallId) {
        completedManageCustomRolesToolCallIds.add(toolCallId);
      }
    }
  }

  for (const message of messages) {
    if (message.role !== 'assistant') {
      continue;
    }

    for (const part of message.parts) {
      if (!isCustomRoleStagingManageCustomRolesToolPart(part)) {
        continue;
      }

      const toolCallId = readManageCustomRolesToolCallId(part);

      if (!toolCallId) {
        continue;
      }

      if (completedManageCustomRolesToolCallIds.has(toolCallId)) {
        continue;
      }

      return true;
    }
  }

  return false;
}

export function readOrderedCustomRoleUpdates({
  messages,
}: {
  messages: TerraUIMessage[];
}): ParsedObjectUpdate[] {
  const orderedUpdates: ParsedObjectUpdate[] = [];
  const seenUpdateIds = new Set<string>();

  for (const message of messages) {
    for (const part of message.parts) {
      for (const update of readObjectUpdatesFromPart(part, message.id)) {
        if (update.objectKind !== CUSTOM_ROLE_OBJECT_KIND) {
          continue;
        }

        if (seenUpdateIds.has(update.id)) {
          continue;
        }

        seenUpdateIds.add(update.id);
        orderedUpdates.push(update);
      }
    }
  }

  return orderedUpdates;
}

function readManageCustomRolesToolInput(
  part: TerraUIMessage['parts'][number],
): Record<string, unknown> | null {
  if (part.type.startsWith('tool-')) {
    const toolName = normalizeToolName(part.type.replace(/^tool-/, ''));

    if (toolName !== MANAGE_CUSTOM_ROLES_TOOL) {
      return null;
    }

    return toJsonRecord(readObjectValue(part, 'input'));
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return null;
  }

  const data = part.data;

  if (!data || !isManageCustomRolesToolCallData(data)) {
    return null;
  }

  return toJsonRecord(data.input);
}

function isCustomRoleStagingManageCustomRolesToolPart(
  part: TerraUIMessage['parts'][number],
): boolean {
  const input = readManageCustomRolesToolInput(part);

  if (!input) {
    return false;
  }

  const action = readOptionalString(readObjectValue(input, 'action'));

  if (action !== 'update') {
    return false;
  }

  const target =
    readOptionalString(readObjectValue(input, 'target')) ?? 'custom_role';

  return target === 'custom_role';
}

function isManageCustomRolesToolCallCompletePart(
  part: TerraUIMessage['parts'][number],
): boolean {
  if (part.type.startsWith('tool-')) {
    const toolName = normalizeToolName(part.type.replace(/^tool-/, ''));

    if (toolName !== MANAGE_CUSTOM_ROLES_TOOL) {
      return false;
    }

    const output =
      readObjectValue(part, 'output') ?? readObjectValue(part, 'result');

    return output !== undefined && output !== null;
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return false;
  }

  const data = part.data;

  if (!data || !isManageCustomRolesToolCallData(data)) {
    return false;
  }

  if (data.type === DataMessageTypeEnum.ToolCallComplete) {
    return true;
  }

  const status = readOptionalString(data.status);

  return (
    status === 'completed' ||
    status === 'result' ||
    status === 'error' ||
    status === 'aborted'
  );
}

function readManageCustomRolesToolCallId(
  part: TerraUIMessage['parts'][number],
): string | null {
  if (part.type.startsWith('tool-')) {
    const toolName = normalizeToolName(part.type.replace(/^tool-/, ''));

    if (toolName !== MANAGE_CUSTOM_ROLES_TOOL) {
      return null;
    }

    return (
      readOptionalString(readObjectValue(part, 'toolCallId')) ??
      readOptionalString(readObjectValue(part, 'id')) ??
      part.type
    );
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return null;
  }

  const data = part.data;

  if (!data || !isManageCustomRolesToolCallData(data)) {
    return null;
  }

  const toolCallId = readOptionalString(data.toolCallId);

  return toolCallId ?? null;
}

function isManageCustomRolesToolCallData(
  data: DataMessageType,
): data is ToolCallGenericMessage {
  const toolName = normalizeToolName((data as ToolCallGenericMessage).toolName);

  return (
    [
      DataMessageTypeEnum.ToolCallComplete,
      DataMessageTypeEnum.ToolCallUpdate,
      DataMessageTypeEnum.ToolCall,
    ].includes(data.type) && toolName === MANAGE_CUSTOM_ROLES_TOOL
  );
}

function readObjectUpdatesFromPart(
  part: TerraUIMessage['parts'][number],
  messageId: string,
): ParsedObjectUpdate[] {
  const sdkToolUpdates = readSdkToolObjectUpdates(part, messageId);

  if (sdkToolUpdates.length > 0) {
    return sdkToolUpdates;
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return [];
  }

  const data = part.data;

  if (!data || !isObjectUpdateToolCallData(data)) {
    return [];
  }

  const proposals = readObjectUpdateProposalsFromToolPayload({
    output: data.output,
    input: data.input,
  });

  if (proposals.length === 0) {
    return [];
  }

  return proposals.map((proposal) => ({
    id: buildStableObjectUpdateId({
      toolCallId: data.toolCallId,
      objectKind: proposal.objectKind,
      objectId: proposal.objectId,
    }),
    ...proposal,
  }));
}

function isObjectUpdateToolCallData(
  data: DataMessageType,
): data is ToolCallGenericMessage {
  const toolName = normalizeToolName((data as ToolCallGenericMessage).toolName);

  return (
    [
      DataMessageTypeEnum.ToolCallComplete,
      DataMessageTypeEnum.ToolCallUpdate,
      DataMessageTypeEnum.ToolCall,
    ].includes(data.type) && OBJECT_UPDATE_TOOL_NAMES.has(toolName)
  );
}

function readSdkToolObjectUpdates(
  part: TerraUIMessage['parts'][number],
  _messageId: string,
): ParsedObjectUpdate[] {
  if (!part.type.startsWith('tool-')) {
    return [];
  }

  const toolName = normalizeToolName(part.type.replace(/^tool-/, ''));

  if (!OBJECT_UPDATE_TOOL_NAMES.has(toolName)) {
    return [];
  }

  const proposals = readObjectUpdateProposalsFromToolPayload({
    output: readObjectValue(part, 'output') ?? readObjectValue(part, 'result'),
    input: readObjectValue(part, 'input'),
  });

  if (proposals.length === 0) {
    return [];
  }

  const toolCallId =
    readOptionalString(readObjectValue(part, 'toolCallId')) ??
    readOptionalString(readObjectValue(part, 'id')) ??
    part.type;

  return proposals.map((proposal) => ({
    id: buildStableObjectUpdateId({
      toolCallId,
      objectKind: proposal.objectKind,
      objectId: proposal.objectId,
    }),
    ...proposal,
  }));
}

function readObjectUpdateProposalsFromToolPayload({
  output,
  input,
}: {
  output: unknown;
  input: unknown;
}): ObjectUpdateProposal[] {
  const stagedProposals = parseStagedObjectUpdateProposals({ output, input });

  if (stagedProposals.length > 0) {
    return stagedProposals;
  }

  const directProposal =
    parseObjectUpdateOutput(output) ?? parseObjectUpdateOutput(input);

  if (directProposal) {
    return [directProposal];
  }

  return [];
}

function parseStagedObjectUpdateProposals({
  output,
  input,
}: {
  output: unknown;
  input: unknown;
}): ObjectUpdateProposal[] {
  const outputRecord = toJsonRecord(output);

  if (!outputRecord || outputRecord.staged !== true) {
    return [];
  }

  const proposalsValue = readObjectValue(outputRecord, 'proposals');

  if (Array.isArray(proposalsValue)) {
    return proposalsValue
      .map((entry) => parseObjectUpdateOutput(entry))
      .filter(
        (proposal): proposal is ObjectUpdateProposal => proposal !== null,
      );
  }

  const nestedProposal = parseObjectUpdateOutput(
    readObjectValue(outputRecord, 'proposal'),
  );

  if (nestedProposal) {
    return [nestedProposal];
  }

  const objectKind = readOptionalString(
    readObjectValue(outputRecord, 'objectKind'),
  );

  if (!isChatEditableObjectKind(objectKind)) {
    return [];
  }

  const updatedFieldsValue = readObjectValue(outputRecord, 'updatedFields');
  const updatedFields = Array.isArray(updatedFieldsValue)
    ? updatedFieldsValue.filter(
        (field): field is string =>
          typeof field === 'string' && field.trim().length > 0,
      )
    : [];

  const inputRecord = toJsonRecord(input);
  const patch = buildPatchFromStagedToolInput({
    objectKind,
    input: inputRecord,
    updatedFields,
  });

  if (Object.keys(patch).length === 0) {
    return [];
  }

  const reason =
    readOptionalString(inputRecord?.reason) ??
    'The co-pilot updated the draft.';

  const credentialIdsValue = readObjectValue(outputRecord, 'credentialIds');

  if (
    objectKind === CREDENTIAL_OBJECT_KIND &&
    Array.isArray(credentialIdsValue)
  ) {
    const credentialIds = credentialIdsValue.filter(
      (credentialId): credentialId is string =>
        typeof credentialId === 'string' && credentialId.trim().length > 0,
    );

    if (credentialIds.length > 0) {
      return credentialIds.map((credentialId) => ({
        type: 'object_update_proposal',
        objectKind,
        objectId: credentialId,
        patch,
        reason,
      }));
    }
  }

  const objectId = readOptionalString(
    readObjectValue(outputRecord, 'objectId'),
  );

  if (!objectId) {
    return [];
  }

  return [
    {
      type: 'object_update_proposal',
      objectKind,
      objectId,
      patch,
      reason,
    },
  ];
}

function buildPatchFromStagedToolInput({
  objectKind,
  input,
  updatedFields,
}: {
  objectKind: ChatEditableObjectKind;
  input: Record<string, unknown> | null;
  updatedFields: string[];
}): Record<string, unknown> {
  if (!input) {
    return {};
  }

  const patch: Record<string, unknown> = {};
  const fieldsToCopy =
    updatedFields.length > 0
      ? updatedFields
      : objectKind === CUSTOM_ROLE_OBJECT_KIND
        ? ['name', 'description']
        : objectKind === CREDENTIAL_OBJECT_KIND
          ? ['role', 'roleId', 'roleDescription', 'headers', 'cookies']
          : [];

  for (const field of fieldsToCopy) {
    const value = readObjectValue(input, field);

    if (value === undefined) {
      continue;
    }

    patch[field] = value;
  }

  if (
    objectKind === CUSTOM_ROLE_OBJECT_KIND &&
    patch.name === undefined &&
    readOptionalString(readObjectValue(input, 'roleId')) ===
      CUSTOM_ROLE_CREATE_DRAFT_ID
  ) {
    const createRoleName = readOptionalString(
      readObjectValue(input, 'roleName'),
    );

    if (createRoleName) {
      patch.name = createRoleName;
    }
  }

  if (
    objectKind === CREDENTIAL_OBJECT_KIND &&
    patch.roleDescription === undefined
  ) {
    const descriptionValue = readObjectValue(input, 'description');

    if (descriptionValue !== undefined) {
      patch.roleDescription = descriptionValue;
    }
  }

  return patch;
}

function toJsonRecord(value: unknown): Record<string, unknown> | null {
  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  return parsed as Record<string, unknown>;
}

function parseObjectUpdateOutput(
  output: unknown,
  depth = 0,
): ObjectUpdateProposal | null {
  if (depth > MAX_OBJECT_UPDATE_PARSE_DEPTH) {
    return null;
  }

  const value = typeof output === 'string' ? safeJsonParse(output) : output;

  if (!value || typeof value !== 'object') {
    return null;
  }

  const type = readObjectValue(value, 'type');

  if (type !== 'object_update_proposal') {
    const nestedOutput =
      readObjectValue(value, 'output') ??
      readObjectValue(value, 'result') ??
      readObjectValue(value, 'content');

    if (nestedOutput !== undefined && nestedOutput !== output) {
      return parseObjectUpdateOutput(nestedOutput, depth + 1);
    }
  }

  if (type !== 'object_update_proposal') {
    return null;
  }

  const objectKind = readOptionalString(readObjectValue(value, 'objectKind'));
  const objectId = readOptionalString(readObjectValue(value, 'objectId'));
  const reason =
    readOptionalString(readObjectValue(value, 'reason')) ??
    'The co-pilot updated the draft.';
  const patchValue = readObjectValue(value, 'patch');

  if (
    !isChatEditableObjectKind(objectKind) ||
    !objectId ||
    !patchValue ||
    typeof patchValue !== 'object'
  ) {
    return null;
  }

  const patch = patchValue as Record<string, unknown>;

  if (Object.keys(patch).length === 0) {
    return null;
  }

  return {
    type: 'object_update_proposal',
    objectKind,
    objectId,
    patch,
    reason,
  };
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
}

function normalizeToolName(value: string | undefined): string {
  return (value ?? '').replace(/_/g, '-').toLowerCase();
}
